// =============================================================================
// Strings for the home page, feed, notifications, files and project pages
// =============================================================================
//
// These live here rather than in translations.js on purpose. That file is
// 80 KB of a single flat object; adding keys to it means rewriting the whole
// thing, and every future feature would pay the same toll. Strings that belong
// to one feature are easier to keep correct next to it.
//
// Same shape as the main translations, so a key can be lifted across later
// without changing the call sites.
//
// TONE. Every string here is a label in a business tool. It states what a
// thing is or what happened, in as few words as will do it. It does not
// address the reader, does not explain itself, does not encourage, and is
// never written in the first person: the software has no "I". "Nessun
// aggiornamento", not "Nessuno ha ancora scritto niente qui"; "Salvataggio non
// riuscito", not "Non sono riuscito a salvare".
// =============================================================================

const STRINGS = {
  en: {
    "common.editShort": "Edit",
    // Marks a field that can be left alone. Only a project's title is
    // required, and unmarked the create dialog reads as four things to fill in
    // before a project can exist - which is its own reason to skip the ones
    // that look like work.
    "common.optional": "optional",

    "home.projectsHint": "Active projects and projects that have stalled",

    "cal.title": "Calendar",

    "date.choose": "Choose a date",
    "date.today": "Today",
    "date.clear": "Clear",

    "decay.searchElement": "Search by symbol or name",
    "decay.noElementMatch": "No results",

    "digest.label": "Email me the weekly activity summary",
    "digest.on": "Weekly summary enabled",
    "digest.off": "Weekly summary disabled",
    "digest.failed": "Preference not saved",
    "digest.testSend": "Send test",
    "digest.testHint": "Sends this week's summary to {email}",
    "digest.testSent": "Test sent to {email}",
    "digest.testFailed": "Test email not sent",

    "news.title": "Field news",
    "news.all": "All",
    "news.research": "Research",
    "news.industry": "Industry",
    "news.emptyFilter": "No news",

    "feed.headingLatest": "Latest",
    "feed.headingQuiet": "Stalled",
    "feed.headingNew": "New projects",
    "feed.quietSummary": "{count} projects with no recent activity",
    "feed.quietFor": "stalled for {days} days",
    "feed.waitingOn": "Waiting on",
    "feed.newProject": "New project",
    "feed.empty": "No activity",
    "feed.emptyHint": "Project updates appear here.",
    "feed.justNow": "today",
    "feed.yesterday": "yesterday",
    "feed.previous": "Previous",
    "feed.next": "Next",
    "feed.recentActivity": "Recent activity",
    "feed.openProject": "Open",
    "feed.noUpdatesYet": "No updates",

    "notif.title": "Notifications",
    "notif.markAll": "Mark all read",
    "notif.empty": "No notifications",
    "notif.newProject": "{name} created a new project",
    "notif.addedYou": "{name} added you to",
    "notif.updateOne": "{name} wrote an update",
    "notif.updatesMany": "{count} new updates",
    "notif.reminderDue": "Reminder due",

    "files.title": "Files",
    "files.back": "Back to home",
    "files.search": "Search files or projects",
    "files.summary": "{count} files, {size} in total",
    "files.groupSummary": "{count} \u00b7 {size}",
    "files.sectionProjects": "Projects",
    "files.sectionPlanning": "Planning",
    "files.empty": "No files",
    "files.noMatch": "No results",
    "files.closed": "closed",
    "files.loadFailed": "Files not loaded",
    "files.deleteTitle": "Delete this file?",
    "files.deleteDesc":
      "{name} will be removed from storage permanently. This cannot be undone.",
    "files.cancel": "Cancel",
    "files.delete": "Delete",

    "proj.statusWaiting": "Waiting",
    "proj.silentDays": "stalled {days}d",
    "proj.today": "active today",
    "proj.people": "People involved",
    "proj.noPeople": "None",
    "proj.waitingLabel": "Waiting for",
    "proj.waitingPlaceholder": "e.g. quote from the supplier",
    "proj.waitingClear": "Clear",
    "proj.waitingSet": "Save",
    "proj.addWaiting": "Add a waiting note",
    "proj.lastActivity": "Last activity",
    "proj.mine": "My projects",
    "proj.others": "Other projects",
    "proj.color": "Colour",
    "proj.editTitle": "Edit project",

    // Confirmations: what changed, nothing more.
    "ok.projectSaved": "Project saved",
    "ok.peopleSaved": "People updated",
    "ok.waitingSaved": "Note saved",
    "ok.waitingCleared": "Note removed",
    "ok.updatePosted": "Update posted",
    "ok.updateDeleted": "Update deleted",
    "ok.fileUploaded": "File uploaded",
    "ok.fileDeleted": "File deleted",
    "ok.projectDeleted": "Project deleted",
    "ok.meetingCreated": "Meeting added",
    "ok.meetingDeleted": "Meeting deleted",

    // Failures: what did not happen, in the same voice.
    "err.projectSave": "Project not saved",
    "err.peopleSave": "People not updated",
    "err.waitingSave": "Note not saved",
    "err.updatePost": "Update not posted",
    "err.updateDelete": "Update not deleted",
    "err.fileUpload": "File not uploaded",
    "err.fileDelete": "File not deleted",
    "err.fileDownload": "File not downloaded",
    "err.filePreview": "Preview not available",
    "err.projectDelete": "Project not deleted",
    "err.meetingCreate": "Meeting not added",
    "err.meetingDelete": "Meeting not deleted",
    "err.projectLoad": "Project not loaded",
  },
  it: {
    "common.editShort": "Modifica",
    "common.optional": "facoltativo",

    "home.projectsHint": "Progetti attivi e progetti fermi",

    "cal.title": "Calendario",

    "date.choose": "Scegli una data",
    "date.today": "Oggi",
    "date.clear": "Cancella",

    "decay.searchElement": "Cerca per simbolo o nome",
    "decay.noElementMatch": "Nessun risultato",

    "digest.label": "Inviami il riepilogo settimanale delle attivit\u00e0 via email",
    "digest.on": "Riepilogo settimanale attivato",
    "digest.off": "Riepilogo settimanale disattivato",
    "digest.failed": "Preferenza non salvata",
    "digest.testSend": "Invia prova",
    "digest.testHint": "Invia il riepilogo di questa settimana a {email}",
    "digest.testSent": "Prova inviata a {email}",
    "digest.testFailed": "Email di prova non inviata",

    "news.title": "Notizie dal settore",
    "news.all": "Tutte",
    "news.research": "Ricerca",
    "news.industry": "Industria",
    "news.emptyFilter": "Nessuna notizia",

    "feed.headingLatest": "Ultimi aggiornamenti",
    "feed.headingQuiet": "Fermi da tempo",
    "feed.headingNew": "Nuovi progetti",
    "feed.quietSummary": "{count} progetti senza attivit\u00e0 recente",
    "feed.quietFor": "fermo da {days} giorni",
    "feed.waitingOn": "In attesa di",
    "feed.newProject": "Nuovo progetto",
    "feed.empty": "Nessuna attivit\u00e0",
    "feed.emptyHint": "Gli aggiornamenti dei progetti compaiono qui.",
    "feed.justNow": "oggi",
    "feed.yesterday": "ieri",
    "feed.previous": "Precedente",
    "feed.next": "Successivo",
    "feed.recentActivity": "Ultimi aggiornamenti",
    "feed.openProject": "Apri",
    "feed.noUpdatesYet": "Nessun aggiornamento",

    "notif.title": "Notifiche",
    "notif.markAll": "Segna tutte come lette",
    "notif.empty": "Nessuna notifica",
    "notif.newProject": "{name} ha creato un nuovo progetto",
    "notif.addedYou": "{name} ti ha aggiunto a",
    "notif.updateOne": "{name} ha scritto un aggiornamento",
    "notif.updatesMany": "{count} nuovi aggiornamenti",
    "notif.reminderDue": "Promemoria in scadenza",

    "files.title": "File",
    "files.back": "Torna alla home",
    "files.search": "Cerca file o progetti",
    "files.summary": "{count} file, {size} in totale",
    "files.groupSummary": "{count} \u00b7 {size}",
    "files.sectionProjects": "Progetti",
    "files.sectionPlanning": "Planning",
    "files.empty": "Nessun file",
    "files.noMatch": "Nessun risultato",
    "files.closed": "chiuso",
    "files.loadFailed": "File non caricati",
    "files.deleteTitle": "Eliminare questo file?",
    "files.deleteDesc":
      "{name} verr\u00e0 rimosso definitivamente dallo storage. L'operazione non si pu\u00f2 annullare.",
    "files.cancel": "Annulla",
    "files.delete": "Elimina",

    "proj.statusWaiting": "In attesa",
    "proj.silentDays": "fermo da {days}g",
    "proj.today": "attivo oggi",
    "proj.people": "Persone coinvolte",
    "proj.noPeople": "Nessuna",
    "proj.waitingLabel": "In attesa di",
    "proj.waitingPlaceholder": "es. preventivo dal fornitore",
    "proj.waitingClear": "Rimuovi",
    "proj.waitingSet": "Salva",
    "proj.addWaiting": "Aggiungi una nota di attesa",
    "proj.lastActivity": "Ultima attivit\u00e0",
    "proj.mine": "I miei progetti",
    "proj.others": "Altri progetti",
    "proj.color": "Colore",
    "proj.editTitle": "Modifica progetto",

    "ok.projectSaved": "Progetto salvato",
    "ok.peopleSaved": "Persone aggiornate",
    "ok.waitingSaved": "Nota salvata",
    "ok.waitingCleared": "Nota rimossa",
    "ok.updatePosted": "Aggiornamento pubblicato",
    "ok.updateDeleted": "Aggiornamento eliminato",
    "ok.fileUploaded": "File caricato",
    "ok.fileDeleted": "File eliminato",
    "ok.projectDeleted": "Progetto eliminato",
    "ok.meetingCreated": "Incontro aggiunto",
    "ok.meetingDeleted": "Incontro eliminato",

    "err.projectSave": "Progetto non salvato",
    "err.peopleSave": "Persone non aggiornate",
    "err.waitingSave": "Nota non salvata",
    "err.updatePost": "Aggiornamento non pubblicato",
    "err.updateDelete": "Aggiornamento non eliminato",
    "err.fileUpload": "File non caricato",
    "err.fileDelete": "File non eliminato",
    "err.fileDownload": "File non scaricato",
    "err.filePreview": "Anteprima non disponibile",
    "err.projectDelete": "Progetto non eliminato",
    "err.meetingCreate": "Incontro non aggiunto",
    "err.meetingDelete": "Incontro non eliminato",
    "err.projectLoad": "Progetto non caricato",
  },
};

/**
 * Look up a string for a language, falling back to English and then to the
 * key itself, so a missing translation degrades to something readable rather
 * than to blank space.
 *
 * Values in `vars` replace {placeholders} in the string.
 */
export function localStrings(language) {
  const table = STRINGS[language] || STRINGS.en;
  return (key, vars) => {
    let text = table[key] ?? STRINGS.en[key] ?? key;
    if (vars) {
      for (const [name, value] of Object.entries(vars)) {
        text = text.replace(`{${name}}`, String(value));
      }
    }
    return text;
  };
}

/**
 * The name somebody is greeted by.
 *
 * Accounts hold a full name because that is what is needed everywhere a person
 * has to be identified. A greeting is the one place that is wrong: nobody says
 * "welcome back, Adriano Giovanni Mistretta".
 *
 * The first word, so a double first name gives the first of the two.
 */
export function firstName(displayName) {
  return (displayName || "").trim().split(/\s+/)[0] || "";
}

export default STRINGS;
